import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import {
  ShoppingCart,
  Package,
  Users,
  LogOut,
  Menu,
  Bell,
  Search,
  FileText,
  Calendar,
  DollarSign,
  BarChart3,
  Waves,
  ChevronLeft,
  ChevronRight,
  Truck,
  ClipboardList,
  AlertCircle,
  BookOpen,
  UserCheck,
  Crown,
  Building,
  Palette,
  Table,
  Clock
} from 'lucide-react';
import SelectorEmpresaNOBUTTON from './SelectorEmpresaNOBUTTON';
import SuperAdminCompanySelector from './SuperAdminCompanySelector';
import ThemeCustomizer from './ThemeCustomizer';
import { useEmpresa } from '../contexts/EmpresaContext';
import '../styles/Layout.css';

const Layout = ({ children }) => {
  // Detectar móvil incluyendo landscape
  const detectMobile = () => {
    return window.innerWidth <= 768 ||
           (window.innerWidth <= 950 && window.innerHeight <= 600);
  };

  const isMobileInitial = detectMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobileInitial); // Cerrado en móvil por defecto
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileInitial);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSuperAdmin } = useSelector(state => state.auth);
  const { empresaActiva, cambiarEmpresa } = useEmpresa();

  const dispatch = useDispatch();

  // Ref para evitar aplicaciones duplicadas de tema
  const lastPathRef = useRef(null);

  // Detectar si es móvil
  useEffect(() => {
    const handleResize = () => {
      const mobile = detectMobile();
      setIsMobile(mobile);
      // Si cambiamos a escritorio, abrir sidebar
      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Bloquear scroll del body cuando sidebar está abierto en móvil
  useEffect(() => {
    const isMobile = detectMobile();

    if (isMobile && sidebarOpen) {
      // Bloquear scroll del body para evitar que aparezca la barra del navegador
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.touchAction = 'none';
    } else if (isMobile && !sidebarOpen) {
      // Restaurar scroll del body
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    };
  }, [sidebarOpen]);

  // Ocultar header y bloquear scroll cuando modal de tema está activo en móvil
  useEffect(() => {
    const isMobile = detectMobile();
    const header = document.querySelector('.layout-header');

    if (isMobile) {
      if (themeModalOpen) {
        // Ocultar header
        if (header) {
          header.style.display = 'none';
        }
        // Bloquear scroll del body
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        // Mostrar header
        if (header) {
          header.style.display = '';
        }
        // Restaurar scroll del body
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    }

    return () => {
      if (header) {
        header.style.display = '';
      }
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [themeModalOpen]);

  // Aplicar colores del tema al cargar, cuando cambie la ubicación, y cuando se actualice el tema
  useEffect(() => {
    // Evitar aplicaciones duplicadas en la misma ruta (StrictMode)
    if (lastPathRef.current === location.pathname) {
      return;
    }
    lastPathRef.current = location.pathname;

    const applyColors = () => {
      const savedPrimary = localStorage.getItem('theme-primary-color') || '#c4fffc';
      const savedSecondary = localStorage.getItem('theme-secondary-color') || '#000000';

      console.log('🎨 Aplicando colores del tema:', { savedPrimary, savedSecondary });

      // Aplicar color primario
      const root = document.documentElement;
      root.style.setProperty('--theme-primary', savedPrimary);
      root.style.setProperty('--theme-secondary', savedSecondary);

      // Generar variaciones de color
      const adjustColor = (color, percent) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
          (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
          (B < 255 ? B < 1 ? 0 : B : 255))
          .toString(16).slice(1);
      };

      root.style.setProperty('--theme-primary-light', adjustColor(savedPrimary, 20));
      root.style.setProperty('--theme-primary-dark', adjustColor(savedPrimary, -20));
      root.style.setProperty('--theme-primary-darker', adjustColor(savedPrimary, -30));

      // Aplicar fondos a todos los contenedores
      const applyThemeColors = () => {
        // Aplicar color sólido a layout-content
        document.querySelectorAll('.layout-content').forEach(el => {
          el.style.background = savedPrimary;
        });

        // Aplicar color sólido a dashboard-content
        document.querySelectorAll('.dashboard-content').forEach(el => {
          el.style.background = savedPrimary;
        });

        // Aplicar color sólido a todas las páginas
        document.querySelectorAll('.cotizaciones-container, .clientes-container, .equipo-container, .ordenes-venta-container, .ordenes-compra-container, .proveedores-container, .finanzas-container, .calendario-container, .manual-container, .perfil-container, .configuracion-container, .urgencias-container, .transito-container, .empresa-container, .super-admin-container, .configuracion-empresa-container').forEach(el => {
          el.style.background = savedPrimary;
        });

        // Aplicar hover y color a nav items - NO clonar elementos
        document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => {
          el.style.setProperty('--hover-bg', `${savedPrimary}26`);

          // Si NO está activo, aplicar color del tema
          if (!el.classList.contains('active')) {
            el.style.setProperty('color', savedPrimary, 'important');
            el.style.setProperty('border-color', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.3)`, 'important');
            el.style.opacity = '0.8';

            // Aplicar color a los SVG también
            const svg = el.querySelector('svg');
            if (svg) {
              svg.style.setProperty('color', savedPrimary, 'important');
            }

            // Aplicar color al span de texto explícitamente
            const span = el.querySelector('span');
            if (span) {
              span.style.setProperty('color', savedPrimary, 'important');
            }
          } else {
            // Si está activo, usar color oscuro del tema para el borde
            el.style.setProperty('color', savedSecondary, 'important');
            // Borde más grueso y oscuro para la opción activa
            el.style.setProperty('border-left-width', '4px', 'important');
            el.style.setProperty('border-color', savedSecondary, 'important');
            // Agregar sombra interna para mejor contraste
            el.style.setProperty('box-shadow', `inset 4px 0 0 ${savedSecondary}, inset 0 0 0 1px rgba(0,0,0,0.1)`, 'important');
            el.style.opacity = '1';

            const svg = el.querySelector('svg');
            if (svg) {
              svg.style.setProperty('color', savedSecondary, 'important');
            }

            const span = el.querySelector('span');
            if (span) {
              span.style.setProperty('color', savedSecondary, 'important');
            }
          }
        });

        // Aplicar colores a los botones del sidebar footer
        const sidebarToggle = document.querySelector('.sidebar-toggle');
        if (sidebarToggle) {
          sidebarToggle.style.setProperty('background', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.1)`, 'important');
          sidebarToggle.style.setProperty('border-color', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.3)`, 'important');
          sidebarToggle.style.setProperty('color', savedPrimary, 'important');

          const toggleSvg = sidebarToggle.querySelector('svg');
          if (toggleSvg) {
            toggleSvg.style.setProperty('color', savedPrimary, 'important');
          }

          const toggleSpan = sidebarToggle.querySelector('span');
          if (toggleSpan) {
            toggleSpan.style.setProperty('color', savedPrimary, 'important');
          }
        }

        const logoutBtn = document.querySelector('.logout-btn');
        if (logoutBtn) {
          logoutBtn.style.setProperty('background', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.1)`, 'important');
          logoutBtn.style.setProperty('border-color', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.3)`, 'important');
          logoutBtn.style.setProperty('color', savedPrimary, 'important');

          const logoutSvg = logoutBtn.querySelector('svg');
          if (logoutSvg) {
            logoutSvg.style.setProperty('color', savedPrimary, 'important');
          }

          const logoutSpan = logoutBtn.querySelector('span');
          if (logoutSpan) {
            logoutSpan.style.setProperty('color', savedPrimary, 'important');
          }
        }

        // Aplicar colores a los bordes del sidebar-header y sidebar-footer
        const sidebarHeader = document.querySelector('.sidebar-header');
        if (sidebarHeader) {
          sidebarHeader.style.setProperty('border-bottom-color', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.2)`, 'important');
        }

        const sidebarFooter = document.querySelector('.sidebar-footer');
        if (sidebarFooter) {
          sidebarFooter.style.setProperty('border-top-color', `rgba(${parseInt(savedPrimary.slice(1,3), 16)}, ${parseInt(savedPrimary.slice(3,5), 16)}, ${parseInt(savedPrimary.slice(5,7), 16)}, 0.2)`, 'important');
        }
      };

      // Aplicar inmediatamente y con delay para asegurar renderizado
      applyThemeColors();
      setTimeout(applyThemeColors, 100);
    };

    // Aplicar colores inicialmente
    applyColors();

    // Escuchar cambios de tema
    const handleThemeChange = () => {
      console.log('🎨 Detectado cambio de tema, re-aplicando colores...');
      applyColors();
    };

    window.addEventListener('theme-color-change', handleThemeChange);

    // Cleanup
    return () => {
      window.removeEventListener('theme-color-change', handleThemeChange);
    };
  }, [location.pathname]); // Re-ejecutar cuando cambie la ruta

  const handleLogout = async () => {
    try {
      // Cerrar menú lateral si está abierto
      setIsOpen(false);

      // Limpiar todo el localStorage
      localStorage.clear();

      // Limpiar sessionStorage
      sessionStorage.clear();

      // Dispatch logout action para limpiar Redux store
      await dispatch(logout());

      // Limpiar caché del navegador si es posible
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      // Forzar navegación con recarga completa
      window.location.href = '/login';
    } catch (error) {
      console.error('Error durante logout:', error);
      // Asegurar que siempre se limpie y redirija
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  // Solo mostrar menú de empresa si no es SuperAdmin o si SuperAdmin está en contexto de empresa
  const shouldShowCompanyMenu = !isSuperAdmin || (isSuperAdmin && empresaActiva);

  // Obtener permisos del usuario actual
  const userPermissions = empresaActiva?.permissions?.sections || empresaActiva?.permisos?.sections || {};
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;

  // Definir todos los items del menú con sus permisos requeridos
  const allMenuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/dashboard', permission: 'dashboard' },
    { icon: Table, label: 'Maestro', path: '/matriz', permission: 'maestro' },
    { icon: Users, label: 'Clientes', path: '/clientes', permission: 'clientes' },
    { icon: Package, label: 'Proveedores', path: '/proveedores', permission: 'proveedores' },
    { icon: UserCheck, label: 'Equipo', path: '/equipo', permission: 'equipo' },
    { icon: Building, label: 'Empresa', path: '/empresa', permission: 'empresa' },
    { icon: Clock, label: 'Historial', path: '/historial', permission: 'historial' },
  ];

  const menuItems = [
    // Menú específico de empresa (filtrado por permisos)
    ...(shouldShowCompanyMenu ?
      allMenuItems.filter(item => {
        // Admin y Editor tienen acceso a todo
        if (userRole === 'admin' || userRole === 'editor' || isSuperAdmin) {
          return true;
        }
        // Miembros solo ven lo que tienen permitido
        return userPermissions[item.permission] === true;
      })
    : []),
    // Manual siempre disponible
    { icon: BookOpen, label: 'Manual', path: '/manual' },
    // Solo mostrar Super Admin si es superadmin
    ...(isSuperAdmin ? [{
      icon: Crown,
      label: 'Super Admin',
      path: '/super-admin',
      superAdmin: true
    }] : []),
  ];

  // Función para manejar clic en item del menú
  const handleMenuItemClick = () => {
    console.log('🔵 Click en menú - cerrando sidebar');
    // Cerrar sidebar siempre al hacer click en un item
    setSidebarOpen(false);
  };

  return (
    <div className="layout-container">
      {/* Overlay para móvil cuando sidebar está abierto */}
      {isMobile && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            <div className="brand-info">
              <img src="/logo-paperboat.png" alt="Paper Boat" className="sidebar-logo" />
              <h2>PAPER BOAT</h2>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path ||
                           (location.pathname === '/' && item.path === '/dashboard');

            return item.superAdmin ? (
              <Link
                key={index}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''} ${item.superAdmin ? 'super-admin' : ''}`}
                onClick={() => {
                  cambiarEmpresa(null); // Limpiar empresa al ir a Super Admin
                  handleMenuItemClick(); // Cerrar sidebar en móvil
                }}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </Link>
            ) : (
              <Link
                key={index}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''} ${item.urgent ? 'urgent' : ''}`}
                onClick={handleMenuItemClick}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            <span>Ocultar Menú</span>
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="layout-header" style={isMobile ? {
          position: 'relative',
          background: 'white'
        } : {}}>
          {!sidebarOpen && (
            <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
              <Menu size={24} />
            </button>
          )}

          {!isMobile && (
            <div className="header-search">
              <Search size={20} />
              <input type="text" placeholder="Buscar órdenes, productos, clientes..." />
            </div>
          )}

          <div className="header-actions" style={isMobile ? { display: 'contents' } : {}}>
            {!isSuperAdmin && empresaActiva && <SelectorEmpresaNOBUTTON />}
            {isSuperAdmin && <SuperAdminCompanySelector />}

            <button
              className="theme-btn"
              onClick={() => setThemeModalOpen(true)}
              title="Personalizar Colores"
            >
              <Palette size={20} />
            </button>

            {/* <button className="notification-btn">
              <Bell size={20} />
              <span className="notification-badge">3</span>
            </button> */}

            <div className="user-menu" onClick={() => navigate('/perfil')}>
              <div className="user-avatar">
                <Waves size={isMobile ? 16 : 20} />
              </div>
              {!isMobile && (
                <div className="user-info">
                  <span className="user-name">
                    {user ? `${user.firstName} ${user.lastName}` : (localStorage.getItem('username') || 'Usuario')}
                  </span>
                  <span className="user-role">
                    {isSuperAdmin ? 'Super Administrador' : (empresaActiva?.nombreFantasia || empresaActiva?.tradeName || '')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="layout-content">
          {children}
        </div>
      </main>

      {/* Theme Customizer Modal */}
      <ThemeCustomizer
        isOpen={themeModalOpen}
        onClose={() => setThemeModalOpen(false)}
      />
    </div>
  );
};

export default Layout;